import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../../../components/Navbar';
import {
  BoltIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

export default function EntryCriteriaPage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to={`${basePath}/help`} className="text-gray-500 hover:text-gray-700">
                Help Center
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li>
              <Link to={`${basePath}/help/best-practices`} className="text-gray-500 hover:text-gray-700">
                Best Practices
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li className="text-gray-900">Entry Criteria Optimization</li>
          </ol>
        </nav>

        {/* Back Button */}
        <Link 
          to={`${basePath}/help/best-practices`} 
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Best Practices
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Optimizing Entry Criteria for Performance
          </h1>
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 text-yellow-800">
              Intermediate
            </span>
            <span>7 min read</span>
            <span>Performance</span>
          </div>
        </div>

        {/* Key Concept */}
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">Performance Impact</h3>
              <p className="text-yellow-800">
                Entry criteria run on EVERY record save. Inefficient criteria can significantly slow down your org.
              </p>
            </div>
          </div>
        </div>

        {/* The Problem */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Why Entry Criteria Matters
          </h2>
          
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <p className="text-gray-700 mb-4">
              Record-triggered flows evaluate their entry criteria on every single record operation, 
              even if the flow won't run. This means:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-red-500 mr-2">•</span>
                <span>Complex criteria slow down ALL record saves, not just the ones that trigger the flow</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2">•</span>
                <span>Multiple flows on the same object compound the performance impact</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2">•</span>
                <span>Bulk operations are especially affected by inefficient criteria</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Good vs Bad Examples */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Entry Criteria Examples</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-red-50 rounded-lg p-6">
              <h3 className="font-semibold text-red-900 mb-3 flex items-center">
                <XCircleIcon className="h-5 w-5 mr-2" />
                Poor Entry Criteria
              </h3>
              <pre className="bg-white p-3 rounded text-sm mb-3 overflow-x-auto">
{`ISCHANGED(Status) AND
(Status = "Closed" OR 
 Status = "Completed" OR
 Status = "Resolved") AND
NOT(CONTAINS(Owner.Name, "Queue")) AND
RecordType.Name = "Support"`}
              </pre>
              <p className="text-red-800 text-sm">
                Problems: Multiple OR conditions, formula functions, cross-object references
              </p>
            </div>
            
            <div className="bg-green-50 rounded-lg p-6">
              <h3 className="font-semibold text-green-900 mb-3 flex items-center">
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                Optimized Entry Criteria
              </h3>
              <pre className="bg-white p-3 rounded text-sm mb-3 overflow-x-auto">
{`$Record.Status__c = "Closed" AND
$Record.RecordTypeId = "012xx0000000ABC" AND
ISCHANGED($Record.Status__c)`}
              </pre>
              <p className="text-green-800 text-sm">
                Better: Direct field checks first, specific values, minimal functions
              </p>
            </div>
          </div>
        </section>

        {/* Optimization Strategies */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Optimization Strategies</h2>
          
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">1. Order Matters</h3>
              <p className="text-gray-700 mb-2">Put the most restrictive conditions first:</p>
              <ul className="space-y-1 text-gray-600 text-sm">
                <li>• Exact field matches (Status = "Closed")</li>
                <li>• Record Type checks</li>
                <li>• ISCHANGED() conditions</li>
                <li>• Complex formulas or cross-object checks last</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">2. Avoid Cross-Object References</h3>
              <p className="text-gray-700 mb-2">Instead of:</p>
              <code className="block bg-gray-100 p-2 rounded text-sm mb-2">Owner.Profile.Name = "Sales"</code>
              <p className="text-gray-700 mb-2">Use a formula field on the object:</p>
              <code className="block bg-gray-100 p-2 rounded text-sm">Owner_Profile_Name__c = "Sales"</code>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">3. Minimize OR Conditions</h3>
              <p className="text-gray-700 mb-2">Instead of multiple OR conditions, consider:</p>
              <ul className="space-y-1 text-gray-600 text-sm">
                <li>• Using a picklist field with specific values</li>
                <li>• Creating a checkbox formula field</li>
                <li>• Using Custom Metadata for value lists</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Special Considerations */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Special Considerations</h2>
          
          <div className="grid gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Bulk Operations</h4>
              <p className="text-blue-800 text-sm">
                Entry criteria are evaluated for every record in a bulk operation. 
                A 0.1 second criteria check becomes 100 seconds for 1000 records.
              </p>
            </div>
            
            <div className="bg-purple-50 rounded-lg p-4">
              <h4 className="font-semibold text-purple-900 mb-2">Multiple Flows</h4>
              <p className="text-purple-800 text-sm">
                Each flow's criteria are evaluated independently. 5 flows = 5x the performance impact.
                Consider consolidating flows when possible.
              </p>
            </div>
          </div>
        </section>

        {/* Quick Reference */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Quick Reference</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-green-700 mb-3">✅ DO</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Check specific field values first</li>
                  <li>• Use indexed fields when possible</li>
                  <li>• Keep criteria simple and direct</li>
                  <li>• Test with bulk data loads</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-red-700 mb-3">❌ DON'T</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Use complex formulas in criteria</li>
                  <li>• Reference multiple parent objects</li>
                  <li>• Use CONTAINS() or wildcards</li>
                  <li>• Chain multiple OR conditions</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <div className="bg-gray-100 rounded-lg p-6 text-center">
          <p className="text-gray-700 mb-4">
            Ready to optimize your flow entry criteria for better performance?
          </p>
          <Link 
            to={`${basePath}/flows`} 
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Analyze Flow Performance
          </Link>
        </div>
      </div>
    </div>
  );
}